import { useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { Button, Image, Loader } from '@storybook';
import { SubscriptionRequiredModal } from 'components';
import { EditModal, UserRolesState } from 'global-stores';
import { useTimezoneDate } from 'hooks';
import {
	Blank,
	Kebab,
	PageHead,
	Pager,
	SearchBox,
	StatusChip,
} from 'views/design-system';
import { PERMISSION_SCREEN, useUserRoles } from 'views/routes-children';
import { ArchiveConfirmationModal } from 'views/sessions';
import {
	AddNewRoleModal,
	InviteModalPage,
	InviteNavigation,
	InviteNavigationState,
} from 'views/user-roles';

import { CreateSubAccountModal, SubUserInviteModal } from '../components';
import { SUBACCOUNT_DEFINATION, SUBACCOUNT_QUESTION } from '../constants';
import { ISubAccountListState, useSubAccounts } from '../hooks';
import { STATUS_CHIP, search, summarise } from './derive';

import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
import 'views/design-system/legacy-dark.scss';
/*
 * The reused modals' stylesheet. CreateSubAccountModal and SubUserInviteModal
 * import no scss of their own — every `.create-sub-account*` /
 * `.user-sub-account*` rule lives in the LEGACY PAGE's sheet, which only
 * loaded because that page imported it. Without this the modals render as
 * bare text (same trap as General Settings' embedded cards). All its
 * selectors are class-scoped to names this deck never uses, so nothing leaks
 * into the page itself.
 */
import '../sub-account.scss';
import './sub-accounts-deck.scss';

const PAGE_SIZE = 15;

/**
 * Sub Accounts on the v2 design system.
 *
 * Everything that DOES something is the old page's own machinery, reused
 * whole: `useSubAccounts()` and the create/invite/users/delete modals it
 * drives are imported unchanged, so the behaviour is v1's by construction.
 * What this file replaces is only the shell around them — header, tiles,
 * search, the table and its row actions. The legacy `sub-account.tsx` is
 * untouched and still exported.
 */
export const SubAccountsDeck = () => {
	const [isExpand, setIsExpand] = useState(false);
	const [typed, setTyped] = useState('');
	const [page, setPage] = useState(1);
	/* The row whose users panel is open, so the panel can repeat the account's
	   own state — it only ever had the user list, and "Joined" there was read
	   as the account being done (SIM-55). */
	const [openRow, setOpenRow] = useState<ISubAccountListState | null>(null);

	const {
		subAccountRows,
		handleCreateBtn,
		handleUser,
		isOpenModal,
		setIsOpenModal,
		isEditAccount,
		editAccount,
		isOpenUserModal,
		handleCloseViewModal,
		isSubUserLoading,
		subUsersList,
		isOpenDeleteModal,
		subAccountLoading,
		handleDeleteSubAcc,
		inviteModalOpen,
		handleClose,
		onSaveRole,
		isApiLoading,
		isDisabled,
		onInviteRole,
		handleOpenDelete,
		handleOpenView,
		onInviteSubAccountUser,
		isAddRoleModalOpen,
		handleCloseModal,
		mapToNavigationPreviousLabel,
		mapToNavigationNextLabel,
		handleNext,
		handleBack,
		onRemoveInvitedUser,
		disableSubAccount,
		handleEditSubAccountUser,
		isSubscriptionExist,
		closeSubscriptionModal,
		handleNavigate,
		openDeleteInvitedUser,
		invitedUserToDelete,
		handleOpenDeleteInvitedUser,
	} = useSubAccounts();

	const inviteNavigation = useRecoilValue(InviteNavigationState);
	const userRoles = useRecoilValue(UserRolesState);
	const editModal = useRecoilValue(EditModal);

	const { formatDate } = useTimezoneDate();
	const { checkUserWritePermission } = useUserRoles();

	const canWrite = useMemo(
		() => checkUserWritePermission(PERMISSION_SCREEN.SubAccount),
		[checkUserWritePermission]
	);

	const rows = useMemo(
		() => search(subAccountRows ?? [], typed),
		[subAccountRows, typed]
	);
	const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
	const shown = useMemo(
		() => rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
		[rows, page]
	);
	const stats = useMemo(() => summarise(subAccountRows ?? []), [subAccountRows]);

	/* v1's InviteModalPage / AddNewRoleModal footers, labels included. */
	const inviteButtonLabel = useMemo(() => {
		switch (inviteNavigation) {
			case InviteNavigation.InviteForm:
				return 'Review';
			case InviteNavigation.InvitePreview:
				return editModal ? 'Update Invite' : 'Send Invite';
			default:
				return '';
		}
	}, [editModal, inviteNavigation]);

	const inviteBackLabel = useMemo(() => {
		switch (inviteNavigation) {
			case InviteNavigation.InviteForm:
				return 'Cancel';
			case InviteNavigation.InvitePreview:
				return 'Back';
			default:
				return '';
		}
	}, [inviteNavigation]);

	const hasAccounts = (subAccountRows ?? []).length > 0;

	const inviteBlockReason = (item: ISubAccountListState) =>
		`Invites open once the account is verified. ${
			STATUS_CHIP[item.approveStatus].explain
		}`;

	return (
		<div className="sv2-scope sv2 sub-deck">
			<PageHead
				title="Sub Accounts"
				sub={
					<>
						{stats.total} {stats.total === 1 ? 'account' : 'accounts'} &middot;
						supplementary accounts under this one, each with its own users
						&middot; read live from the sub-account API
					</>
				}
				action={
					hasAccounts ? (
						<button className="cta" type="button" onClick={() => handleCreateBtn()}>
							Create new
						</button>
					) : undefined
				}
			/>

			{hasAccounts && (
				<div className="strip estrip">
					<div className="tile">
						<div className="l">Sub accounts</div>
						<div className="n">{stats.total}</div>
						<div className="d">under this parent account</div>
					</div>
					<div className="tile">
						<div className="l">Verified</div>
						<div className="n g">{stats.verified}</div>
						<div className="d">primary user approved &middot; can invite users</div>
					</div>
					<div className="tile">
						<div className="l">Awaiting approval</div>
						<div className="n a">{stats.awaiting}</div>
						<div className="d">
							{stats.withPrimaryUser} with the primary user &middot;{' '}
							{stats.withSuperAdmin} with the super admin
						</div>
					</div>
					<div className="tile">
						<div className="l">Users</div>
						<div className="n bl">{stats.users}</div>
						<div className="d">across every sub-account</div>
					</div>
				</div>
			)}

			{hasAccounts && (
				<div className="ctl">
					<SearchBox
						value={typed}
						onChange={v => {
							setTyped(v);
							setPage(1);
						}}
						placeholder="Search name or status"
						title="Searches the sub-account name and its status"
					/>
					<Pager page={page} pages={pages} onChange={setPage} />
				</div>
			)}

			<div className="panel">
				<div className="ph">
					Sub accounts
					<span className="r">
						{rows.length} {rows.length === 1 ? 'account' : 'accounts'}
						{typed ? ` of ${(subAccountRows ?? []).length}` : ''}
					</span>
				</div>

				{subAccountLoading && !hasAccounts ? (
					<div className="sv2-loading">Loading sub accounts…</div>
				) : !hasAccounts ? (
					<Blank
						title="No sub-account added"
						description={
							<>
								You have not created any sub-account yet.
								<button
									className="defn-toggle"
									type="button"
									onClick={() => setIsExpand(v => !v)}
								>
									{SUBACCOUNT_QUESTION}
								</button>
								{isExpand && (
									<span className="defn">{SUBACCOUNT_DEFINATION}</span>
								)}
							</>
						}
						action={
							<button
								className="btn pri"
								type="button"
								onClick={() => handleCreateBtn()}
							>
								Create new
							</button>
						}
					/>
				) : !rows.length ? (
					<div className="sv2-empty">
						No sub-account matches &ldquo;{typed}&rdquo;.
					</div>
				) : (
					<div className="tscroll">
						<table className="stbl satbl">
							<thead>
								<tr>
									<th>Name</th>
									<th>Creation Date</th>
									<th>Users</th>
									<th>Status</th>
									{canWrite && <th className="accol">Actions</th>}
								</tr>
							</thead>
							<tbody>
								{shown.map(item => {
									const chip = STATUS_CHIP[item.approveStatus];
									const approved = item.approveStatus === 'APPROVED';
									return (
										<tr
											key={item._id}
											onClick={() => {
												setOpenRow(item);
												handleUser(item);
											}}
										>
											<td>
												<span className="nm">{item.name || '--'}</span>
											</td>
											<td>{formatDate(item.createdAt, 'MMM dd, yyyy')}</td>
											<td>
												<span className="ucell">
													{item.totalUsers ?? 0}
													<span className="uview">View</span>
												</span>
											</td>
											<td title={chip.explain}>
												<span className="stcell">
													<StatusChip verdict={chip.verdict}>{chip.label}</StatusChip>
													{chip.waitingOn && (
														<span className="who">Waiting on {chip.waitingOn}</span>
													)}
												</span>
											</td>
											{canWrite && (
												<td className="accol">
													<span className="rowacts">
														<button
															className="btn pri rowbtn"
															type="button"
															disabled={!approved}
															title={
																approved ? undefined : inviteBlockReason(item)
															}
															onClick={e => {
																/* the hook types its event as a div's; it only
																 * calls stopPropagation, which both share */
																if (approved)
																	onInviteRole(
																		e as unknown as React.MouseEvent<
																			HTMLDivElement,
																			MouseEvent
																		>,
																		item
																	);
																else e.stopPropagation();
															}}
														>
															Invite user
														</button>
														<Kebab
															items={[
																{
																	label: 'Edit',
																	onClick: () => handleOpenView(item),
																},
																/* v1 renders Delete permanently disabled
																 * (DropOptions isDeleteDisabled) — kept, not
																 * dropped, so the state is visible. */
																{
																	label: 'Delete',
																	disabled: true,
																	hint:
																		'Deleting a sub-account is disabled',
																	onClick: () => handleOpenDelete(item),
																},
															]}
														/>
													</span>
												</td>
											)}
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{hasAccounts && (
				<div className="foot">
					A row opens the account&rsquo;s own users. &ldquo;Invite user&rdquo;
					needs the primary user&rsquo;s access approved by the super admin
					first &mdash; a not-yet-verified account says why on hover.
				</div>
			)}

			{/* ---- v1's modals, mounted unchanged ---- */}
			{isOpenModal && (
				<CreateSubAccountModal
					isOpen={isOpenModal}
					handleCloseModal={() => setIsOpenModal(false)}
					isEditable={isEditAccount}
					subAccDetail={editAccount}
					className="sv2-modal-scope"
				/>
			)}
			{isOpenUserModal && (
				<SubUserInviteModal
					isOpen={isOpenUserModal}
					handleCloseModal={() => handleCloseViewModal()}
					isSubUserLoading={isSubUserLoading}
					subUsersList={subUsersList}
					onInviteSubAccountUser={onInviteSubAccountUser}
					onRemoveInvitedUser={onRemoveInvitedUser}
					handleEditSubAccountUser={handleEditSubAccountUser}
					openDeleteInvitedUser={openDeleteInvitedUser}
					invitedUserToDelete={invitedUserToDelete}
					handleOpenDeleteInvitedUser={handleOpenDeleteInvitedUser}
					loading={subAccountLoading}
					accountStatus={
						openRow ? STATUS_CHIP[openRow.approveStatus] : undefined
					}
				/>
			)}
			{isOpenDeleteModal && (
				<ArchiveConfirmationModal
					description={`Deleting ${editAccount?.name} sub-account will delete all its users.`}
					boldDescription={` Do you want to delete ${editAccount?.name} sub-account from the system?`}
					label="Delete"
					title="Are you sure?"
					visible={isOpenDeleteModal}
					showLoader={subAccountLoading}
					handleModal={handleDeleteSubAcc}
				/>
			)}
			{(userRoles ?? [])?.length > 0 ? (
				<InviteModalPage
					isOpen={inviteModalOpen}
					handleClose={handleClose}
					disableSubAccount={disableSubAccount}
					edit={editModal}
				>
					<div className="ConfigureModal--footer">
						<Button
							label={inviteBackLabel}
							handleClick={handleClose}
							type="button__filled button__filled--secondary"
						/>
						<Button
							label={inviteButtonLabel}
							handleClick={onSaveRole}
							type="button__filled button__filled--primary"
							disabled={isDisabled || isApiLoading}
							loader={
								isApiLoading ? <Loader dimension={14} type="loader" /> : <></>
							}
						/>
					</div>
				</InviteModalPage>
			) : (
				<AddNewRoleModal
					isAddRoleModalOpen={isAddRoleModalOpen}
					handleCloseModel={handleCloseModal}
				>
					<div className="AddNewRoleModal--container__footer">
						<Button
							label={mapToNavigationPreviousLabel}
							type="button__filled--secondary"
							handleClick={handleBack}
						/>
						<Button
							label={mapToNavigationNextLabel}
							type="button__filled--primary"
							handleClick={handleNext}
							disabled={isDisabled || isApiLoading}
							loader={
								isApiLoading ? <Loader dimension={14} type="loader" /> : <></>
							}
						/>
					</div>
				</AddNewRoleModal>
			)}
			{isSubscriptionExist && (
				<SubscriptionRequiredModal
					isModalOpen={isSubscriptionExist}
					closeModal={closeSubscriptionModal}
					handleNavigate={handleNavigate}
				>
					<div className="subscription-required-modal--body">
						<Image fileName="SubscribedIcon.svg" />
						<h2>Upgrade account type</h2>
						<p>
							To access this feature, please upgrade your account type to
							“Entity/Business”. Upgrading will unlock additional
							functionalities and benefits for your account.
						</p>
						<Button
							label="Upgrade now"
							handleClick={handleNavigate}
							type="button__filled button__filled--primary button__large button-ok"
						/>
					</div>
				</SubscriptionRequiredModal>
			)}
		</div>
	);
};

export default SubAccountsDeck;
